import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  useDashboard,
  useDashboards,
  useHome,
  useIsAdmin,
  useSetFeaturedDashboard,
  type HomeState,
} from "@/api/queries";
import { ReadOnlyTiles } from "@/components/reports/ReadOnlyTiles";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ArrowRightIcon, DashboardIcon, PinIcon } from "@/components/icons";
import { useRelativeTime } from "@/lib/time";

/**
 * The deployment's key numbers: one dashboard an administrator chose for
 * everybody's front page (`PUT /home/featured-dashboard`).
 *
 * A pointer, not a copy — the tiles are the dashboard's own, read-only here,
 * refreshed and laid out on the dashboard page. That is also how widget
 * placement on the front page works: arrange the dashboard, and the front
 * page follows.
 */
export function FeaturedDashboard() {
  const { data: home, isLoading } = useHome();
  const isAdmin = useIsAdmin();
  const featured = home?.featured_dashboard ?? null;

  if (isLoading) return null;
  if (!featured) return isAdmin ? <ChooseFeatured /> : null;
  return <Featured featured={featured} isAdmin={isAdmin} />;
}

function Featured({
  featured,
  isAdmin,
}: {
  featured: NonNullable<HomeState["featured_dashboard"]>;
  isAdmin: boolean;
}) {
  const { data: dashboard, isLoading, isError } = useDashboard(featured.id);
  const refreshed = useRelativeTime(dashboard?.last_refreshed_at ?? featured.last_refreshed_at);
  const [changing, setChanging] = useState(false);

  return (
    <section aria-labelledby="featured-title" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="type-caption text-ink-disabled">Key numbers</p>
          <h2 id="featured-title" className="type-h3 text-ink mt-1 truncate">
            {featured.name || "Untitled dashboard"}
          </h2>
          {featured.description ? (
            <p className="type-support text-ink-secondary mt-0.5">{featured.description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {refreshed ? (
            <span className="type-support text-ink-disabled">Refreshed {refreshed}</span>
          ) : null}
          <Link
            to="/dashboards/$dashboardId"
            params={{ dashboardId: featured.id }}
            className="type-support text-accent inline-flex items-center gap-1 hover:underline"
          >
            Open dashboard
            <ArrowRightIcon className="size-3.5" />
          </Link>
          {isAdmin && !changing ? (
            <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>
              Change
            </Button>
          ) : null}
        </div>
      </div>
      {changing ? <ChooseFeatured current={featured.id} onDone={() => setChanging(false)} /> : null}
      {isError ? (
        <p className="type-support text-ink-secondary py-4">
          This dashboard could not be loaded — it may just have been deleted.
        </p>
      ) : isLoading || !dashboard ? (
        <div className="flex items-center gap-2 py-6">
          <Spinner />
          <span className="type-support text-ink-secondary">Loading tiles…</span>
        </div>
      ) : (
        <ReadOnlyTiles
          tiles={dashboard.tiles}
          links={dashboard.links ?? []}
          canFetch
          emptyDetail="This dashboard has no tiles yet. Pin answers to it and they appear here."
        />
      )}
    </section>
  );
}

/**
 * The administrator's picker: which dashboard every analyst sees first.
 * Shown in place of the section when nothing is featured, so the slot
 * explains itself on a fresh deployment — and never to an analyst.
 */
function ChooseFeatured({ current, onDone }: { current?: string; onDone?: () => void }) {
  const { data: dashboards } = useDashboards();
  const setFeatured = useSetFeaturedDashboard();
  const [choice, setChoice] = useState(current ?? "");
  const list = dashboards ?? [];
  const pick = choice || list[0]?.id || "";

  return (
    <section className="border-border rounded-container flex flex-col gap-3 border border-dashed p-4">
      <div className="flex items-start gap-3">
        <span className="bg-accent-subtle text-accent grid size-9 shrink-0 place-items-center rounded-element">
          <DashboardIcon className="size-5" />
        </span>
        <div>
          <p className="type-label text-ink">
            {current ? "Feature a different dashboard" : "Feature a dashboard here"}
          </p>
          <p className="type-support text-ink-secondary mt-0.5">
            Its tiles become the key numbers on everyone's front page, in the layout you give it on
            the dashboard. Only administrators see this.
          </p>
        </div>
      </div>
      {list.length ? (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="featured-pick" className="sr-only">
            Dashboard to feature
          </label>
          <select
            id="featured-pick"
            value={pick}
            onChange={(event) => setChoice(event.target.value)}
            className="control-md bg-card text-ink type-body border-border rounded-element min-w-56 border px-3"
          >
            {list.map((dashboard) => (
              <option key={dashboard.id} value={dashboard.id}>
                {dashboard.name || "Untitled dashboard"} · {dashboard.tile_count} tile
                {dashboard.tile_count === 1 ? "" : "s"}
              </option>
            ))}
          </select>
          <Button
            variant="primary"
            icon={<PinIcon className="size-4" />}
            loading={setFeatured.isPending && setFeatured.variables !== null}
            disabled={!pick || pick === current}
            onClick={() => setFeatured.mutate(pick, { onSuccess: () => onDone?.() })}
          >
            Feature it
          </Button>
          {current ? (
            <>
              <Button
                variant="ghost"
                loading={setFeatured.isPending && setFeatured.variables === null}
                onClick={() => setFeatured.mutate(null, { onSuccess: () => onDone?.() })}
              >
                Stop featuring
              </Button>
              <Button variant="ghost" onClick={() => onDone?.()}>
                Cancel
              </Button>
            </>
          ) : null}
        </div>
      ) : (
        <Link
          to="/dashboards"
          className="type-support text-accent inline-flex items-center gap-1 hover:underline"
        >
          Create a dashboard first
          <ArrowRightIcon className="size-3.5" />
        </Link>
      )}
    </section>
  );
}
